import {
  extractLinkedInIdentifierFromInput,
  isValidLinkedInPublicIdentifier,
} from '@linkedin-url-utils';
import type { Lang } from '@/i18n';

/**
 * L'API Podawaa vit sur un autre domaine que ce site. En dev, le proxy Vite
 * renvoie `/api` vers l'API locale ; en prod, `VITE_NEVERZERO_API_URL` porte
 * l'origine complète. Le `/api` est ajouté ici, jamais dans les appelants —
 * même convention que `api.ts` côté dashboard.
 */
const API_BASE = `${(import.meta.env.VITE_NEVERZERO_API_URL || '').replace(/\/+$/, '')}/api`;

/**
 * Forme plausible d'un identifiant public LinkedIn.
 *
 * `extractLinkedInIdentifierFromInput` retourne l'entrée telle quelle quand
 * elle n'y trouve pas de `/in/` : « bonjour tout le monde » en ressort inchangé.
 * Ce garde-fou-ci n'est donc pas décoratif — sans lui, on enverrait n'importe
 * quelle phrase au checkout. La validation qui fait foi reste côté API.
 */
const PLAUSIBLE_IDENTIFIER = /^[\p{L}\p{N}][\p{L}\p{N}\-_.%]{2,99}$/u;

export function extractProfileIdentifier(input: string): string | null {
  const identifier = extractLinkedInIdentifierFromInput(input.trim());
  if (!identifier || !isValidLinkedInPublicIdentifier(identifier)) return null;
  if (!PLAUSIBLE_IDENTIFIER.test(identifier)) return null;
  return identifier;
}

export interface CheckoutRequest {
  linkedInUrl: string;
  lang: Lang;
}

/**
 * Crée la session Stripe et rend l'URL hébergée par Stripe.
 *
 * L'email n'est PAS demandé ici : c'est Stripe qui le collecte au checkout, et
 * c'est lui qui fait foi pour créer le compte au webhook. Une adresse saisie
 * sur cette page en plus de celle de Stripe créerait deux identités pour un
 * seul acheteur.
 */
export async function createCheckoutSession({ linkedInUrl, lang }: CheckoutRequest): Promise<string> {
  const response = await fetch(`${API_BASE}/neverzero/checkout`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ linkedInUrl, lang }),
  });

  if (!response.ok) {
    throw new Error(`Checkout refused with status ${response.status}`);
  }

  const payload = (await response.json()) as { url?: string };
  if (!payload.url) {
    throw new Error('Checkout response carried no URL');
  }

  return payload.url;
}
